import React from 'react';
import { TrendingUp, Users, Award, ShieldCheck, CheckCircle, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const BecomePartner = () => {
  const { toast } = useToast();
  const [formData, setFormData] = React.useState({
    businessName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    businessType: '',
    experience: '',
    infrastructure: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  try {
    const response = await fetch("https://formspree.io/f/mkoqopvv", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify(formData)
    });
    if (response.ok) {
      toast({
        title: "Application Submitted Successfully!",
        description: "Our team will review your application and contact you within 48 hours.",
      });
      setFormData({
        businessName: '', ownerName: '', email: '', phone: '',
        address: '', city: '', state: '', pincode: '',
        businessType: '', experience: '', infrastructure: '', message: ''
      });
    } else {
      toast({
        title: "Something went wrong!",
        description: "Please try again later.",
      });
    }
  } catch (error) {
    toast({
      title: "Something went wrong!",
      description: "Please check your internet connection.",
    });
  }
  setIsSubmitting(false);
};

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Become Our Partner</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Join our expanding network of healthcare partners and grow your business with quality pharmaceutical products at competitive prices
            </p>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Partner with Vetam Remedies?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the advantages of partnering with a trusted pharmaceutical company
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="border-2 hover:shadow-xl transition-all hover:-translate-y-2 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Best Margins</h3>
                <p className="text-gray-600">Competitive pricing with excellent profit margins for our partners</p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all hover:-translate-y-2 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Products</h3>
                <p className="text-gray-600">WHO-GMP certified medicines with guaranteed quality assurance</p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all hover:-translate-y-2 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Strong Support</h3>
                <p className="text-gray-600">Dedicated support team to help you grow your business</p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all hover:-translate-y-2 group">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Brand Recognition</h3>
                <p className="text-gray-600">Associate with a trusted and reputed pharmaceutical brand</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Partnership Requirements
              </h2>
              <p className="text-lg text-gray-600">
                Here's what we look for in our ideal partners
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4 p-6 bg-white rounded-xl border-2 border-blue-200">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Valid Drug License</h4>
                  <p className="text-gray-700 text-sm">Must possess a valid wholesale or retail drug license</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-white rounded-xl border-2 border-blue-200">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Storage Infrastructure</h4>
                  <p className="text-gray-700 text-sm">Adequate storage facility for pharmaceutical products</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-white rounded-xl border-2 border-blue-200">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Market Reach</h4>
                  <p className="text-gray-700 text-sm">Established network with healthcare professionals</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-white rounded-xl border-2 border-blue-200">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Financial Stability</h4>
                  <p className="text-gray-700 text-sm">Good financial standing and creditworthiness</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-white rounded-xl border-2 border-blue-200">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Professional Team</h4>
                  <p className="text-gray-700 text-sm">Qualified staff for pharmaceutical distribution</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 bg-white rounded-xl border-2 border-blue-200">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Commitment</h4>
                  <p className="text-gray-700 text-sm">Long-term commitment to partnership growth</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Partnership Application
              </h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and our team will get back to you within 48 hours
              </p>
            </div>

            <Card className="border-2">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="businessName">Business Name *</Label>
                      <Input
                        id="businessName"
                        name="businessName"
                        type="text"
                        required
                        value={formData.businessName}
                        onChange={handleChange}
                        placeholder="Enter your business name"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="ownerName">Owner/Contact Person Name *</Label>
                      <Input
                        id="ownerName"
                        name="ownerName"
                        type="text"
                        required
                        value={formData.ownerName}
                        onChange={handleChange}
                        placeholder="Enter owner name"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address">Business Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      required
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Street address"
                      className="mt-2"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        type="text"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="City"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        type="text"
                        required
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="State"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="pincode">Pincode *</Label>
                      <Input
                        id="pincode"
                        name="pincode"
                        type="text"
                        required
                        value={formData.pincode}
                        onChange={handleChange}
                        placeholder="Pincode"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="businessType">Business Type *</Label>
                      <Select
                        value={formData.businessType}
                        onValueChange={(value) => handleSelectChange('businessType', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select business type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="wholesale">Wholesale Distributor</SelectItem>
                          <SelectItem value="retail">Retail Pharmacy</SelectItem>
                          <SelectItem value="hospital">Hospital Pharmacy</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="experience">Years of Experience *</Label>
                      <Select
                        value={formData.experience}
                        onValueChange={(value) => handleSelectChange('experience', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select experience" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0-2">0-2 years</SelectItem>
                          <SelectItem value="3-5">3-5 years</SelectItem>
                          <SelectItem value="6-10">6-10 years</SelectItem>
                          <SelectItem value="10+">10+ years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="infrastructure">Storage Infrastructure Details *</Label>
                    <Textarea
                      id="infrastructure"
                      name="infrastructure"
                      required
                      value={formData.infrastructure}
                      onChange={handleChange}
                      placeholder="Describe your storage facility, capacity, and infrastructure..."
                      rows={3}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Additional Information</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us more about your business, market reach, and why you want to partner with us..."
                      rows={4}
                      className="mt-2"
                    />
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                    <p className="text-sm text-gray-700">
                      <strong>Note:</strong> After submitting this application, our team will review your details and contact you within 48 hours. Please ensure all information provided is accurate.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
                  >
                    {isSubmitting ? (
                      <>Processing Application...</>
                    ) : (
                      <>
                        Submit Application <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BecomePartner;
